import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ControlVariableDefinition } from 'libs/end-to-end/src/types/databases/controls/control-variable';
import { BiasAnalysisOutput } from 'libs/end-to-end/src/bias-analysis';
import { LevelDefinition } from 'libs/end-to-end/src/types/databases/dimensions/level-definition';
import { extractRootCauseTextOutputs } from 'libs/end-to-end/src/root-cause-analysis';
import { isDiversionBarGraphGroupResult } from 'libs/end-to-end/src/root-cause-analysis';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { RootCauseGroupResult } from 'libs/end-to-end/src/root-cause-analysis';
import { Heading } from 'apps/sigma2-ui/src/components/ui/Heading';
import { TabsRadioGroup } from 'apps/sigma2-ui/src/components/ui/TabsRadioSliderGroup';
import { EmptyRootCauseDisparitySection } from 'apps/sigma2-ui/src/pages/FocusAreaPage/EmptyRootCauseDisparitySection';
import { DownloadButton } from 'apps/sigma2-ui/src/components/ui/DownloadButton';
import { ShareLinkModal } from 'apps/sigma2-ui/src/components/ui/ShareLinkModal';
import { useDimensionConfig } from 'apps/sigma2-ui/src/lib/hooks/useDimensionConfig';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { useCustomVizAutoload } from 'apps/sigma2-ui/src/lib/hooks/useCustomVizAutoload';
import { useCustomViz } from 'apps/sigma2-ui/src/components/custom-viz/hooks/useCustomViz';
import { RootCauseBinScatterChart } from 'apps/sigma2-ui/src/pages/FocusAreaPage/RootCauseBinScatterChart';
import { RootCauseDisparityBarChart } from 'apps/sigma2-ui/src/pages/FocusAreaPage/RootCauseDisparityBarChart';
import { RootCauseGenericBarChart } from 'apps/sigma2-ui/src/pages/FocusAreaPage/RootCauseGenericBarChart';
import { RootCauseList } from 'apps/sigma2-ui/src/pages/FocusAreaPage/RootCauseList';
import { ConfigColors } from 'libs/end-to-end/src/types/branding/colors';
import { MyFocusAreaDefinition } from 'libs/end-to-end/src/my-focus-area-definition';
import { MyMetricDefinition } from 'libs/end-to-end/src/my-metric-definition';
import { useExplainImage } from 'apps/sigma2-ui/src/lib/hooks/useExplainImage';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { FlatCard } from 'apps/sigma2-ui/src/components/ui/FlatCard';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { OaxacaBarChart } from 'apps/sigma2-ui/src/pages/FocusAreaPage/OaxacaBarChart';
import { useAnalytics } from 'apps/sigma2-ui/src/lib/hooks/useAnalytics';
import { DownloadableCharts } from 'libs/end-to-end/src/analytics/DownloadChartAnalyticsEvent';
import { RootCauseGraphPartSerializableProps } from 'libs/end-to-end/src/root-cause-analysis';
import { getTranslatedContentAsString } from 'libs/end-to-end/src/translation-content';
import { ThresholdAnalysisBoxPlot } from 'apps/sigma2-ui/src/pages/FocusAreaPage/ThresholdAnalysisBoxPlot';

const CONTROL_VARIABLE_DISPLAY_MAP: { [key: string]: string } = {
  age: 'age',
  entry_wage: 'entry salary',
  entry_salary: 'entry salary',
  education: 'education',
};

/**
 * This function formats the filename for the download button.
 * It will be formatted as follows, to indicate the focus area, title, demographic, and current page:
 * {focusArea}_{title}_{demographic}
 * @example
 * Hiring_Race_African American_Black_1
 * @param activeTabKey - The active tab key
 * @param focusArea - The focus area
 */
const formatFilename = (activeTabKey: string, focusArea: FocusArea) => {
  return `${focusArea}_${activeTabKey}_card`;
};

export const getControlVariableDisplayText = (
  rootCauseGroupResult: RootCauseGroupResult,
  dynamicControlVariables: ControlVariableDefinition[],
) => {
  const controlLower = rootCauseGroupResult.control_variable.toLowerCase();

  const foundDefinition = dynamicControlVariables.find(
    (definition) => definition.columnHeaderValue === controlLower,
  );

  if (!foundDefinition) {
    return isDiversionBarGraphGroupResult(rootCauseGroupResult)
      ? (CONTROL_VARIABLE_DISPLAY_MAP[controlLower] ?? controlLower)
      : '';
  }

  return foundDefinition.displayName;
};

export const DownloadButtonWrapper: React.FC<{
  imageRef?: React.RefObject<HTMLInputElement | null>;
  focusArea: FocusArea;
  activeKeyConfig: LevelDefinition | undefined;
  onDownloadCb?: () => void;
}> = ({ imageRef, focusArea, activeKeyConfig, onDownloadCb }) => {
  if (activeKeyConfig) {
    return (
      <DownloadButton
        getElement={() => imageRef?.current ?? null}
        filename={formatFilename(activeKeyConfig?.displayName, focusArea)}
        onDownloadCb={onDownloadCb}
      />
    );
  } else {
    return <></>;
  }
};

export const RootCauseDisparitySection: React.FC<{
  navigationTabDemoKey: string | null;
  focusArea: MyFocusAreaDefinition;
  metricDefinition: MyMetricDefinition;
  organizationName: string;
  clearNavigationTabDemoKey: () => void;
}> = ({
  navigationTabDemoKey,
  focusArea,
  metricDefinition,
  organizationName,
  clearNavigationTabDemoKey,
}) => {
  const imageRef = React.useRef<HTMLInputElement | null>(null);

  const [showShareModal, setShowShareModal] = useState(false);

  const { getLevelByColumnValue } = useDimensionConfig();

  const context = useCustomViz();

  const language = useLanguage();

  const { data } = useCustomVizAutoload({
    canAnalyse: false,
    onAnalyze: () => {},
    context,
    ResultZodType: BiasAnalysisOutput,
  });

  const insights = useMemo(() => {
    const allInsights = data?.insights ?? [];
    return allInsights.filter((insight) => insight.groupResults.length > 0);
  }, [data]);

  const [activeTabKey, setActiveTabKey] = useState<string | null>(null);
  const [activeItemIndex, setActiveItemIndex] = useState<number | null>(null);
  const [activeGroupResult, setActiveGroupResult] =
    useState<RootCauseGroupResult | null>(null);

  const [didSetDefaults, setDidSetDefaults] = useState(false);

  useEffect(() => {
    if (context.status === 'loading-viz') {
      setDidSetDefaults(false);
    }
  }, [context]);

  useEffect(() => {
    if (insights.length === 0 || didSetDefaults) {
      return;
    }

    setDidSetDefaults(true);
    setActiveTabKey(insights[0].treatmentLevel);
    setActiveItemIndex(0);
    setActiveGroupResult(insights[0].groupResults.at(0) ?? null);

    clearNavigationTabDemoKey();
  }, [clearNavigationTabDemoKey, didSetDefaults, insights]);

  useEffect(() => {
    if (!navigationTabDemoKey) {
      return;
    }

    // Set the active tab if we are actually the owner of the tab.
    setActiveTabKey(navigationTabDemoKey);
    const newValue = insights.findIndex(
      (insight) => insight.treatmentLevel === navigationTabDemoKey,
    );
    if (newValue === -1) {
      return;
    }
    setActiveItemIndex(newValue);
    setActiveGroupResult(insights[newValue].groupResults.at(0) ?? null);
    clearNavigationTabDemoKey();
  }, [clearNavigationTabDemoKey, insights, navigationTabDemoKey]);

  const currentInsight = useMemo(() => {
    if (!activeTabKey) {
      return null;
    }
    return insights.find((insight) => insight.treatmentLevel === activeTabKey);
  }, [activeTabKey, insights]);

  const activeKeyConfig: LevelDefinition | undefined = useMemo(() => {
    if (!currentInsight || !activeTabKey) {
      return undefined;
    }

    return getLevelByColumnValue(currentInsight.dimension, activeTabKey);
  }, [activeTabKey, currentInsight, getLevelByColumnValue]);

  const tabs = useMemo(() => {
    return insights.map((disparityTab) => {
      return {
        tabValue: disparityTab.treatmentLevel,
      };
    });
  }, [insights]);

  const getSectionDescription = useCallback(
    (focusAreaDefinition: MyFocusAreaDefinition, organizationName: string) => {
      return `Disparity in ${focusAreaDefinition.displayName.toLowerCase()} for ${
        activeKeyConfig?.displayName
      } ${metricDefinition.whiteLabeling.subjectPlural} at ${organizationName}`;
    },
    [activeKeyConfig, metricDefinition],
  );

  const sectionDescription = useMemo(() => {
    return getSectionDescription(focusArea, organizationName);
  }, [focusArea, getSectionDescription, organizationName]);

  const explanationHook = useExplainImage({
    ref: imageRef,
    autoExplain: activeGroupResult !== null,
    key: JSON.stringify(activeGroupResult),
  });

  /**
   * This should never happen since we control the tabs, but for TypeScript and thoroughness
   */
  if (
    !currentInsight ||
    !activeTabKey ||
    activeItemIndex === null ||
    !activeGroupResult
  ) {
    return <EmptyRootCauseDisparitySection language={language} />;
  }

  return (
    <div>
      <TabsRadioGroup
        currValue={activeTabKey}
        values={tabs}
        minWidth="auto"
        onChange={(value: any) => {
          if (typeof value === 'string') {
            setActiveTabKey(value);
            const newInsight = insights.find(
              (insight) => insight.treatmentLevel === value,
            );
            if (!newInsight) {
              return;
            }
            setActiveItemIndex(0);
            setActiveGroupResult(newInsight.groupResults.at(0) ?? null);
            clearNavigationTabDemoKey();
          }
        }}
        renderTab={({ value }) => {
          const levelDefinition = getLevelByColumnValue(
            currentInsight.dimension,
            value.tabValue,
          );

          if (!levelDefinition) {
            return null;
          }

          const checked = levelDefinition.columnValue === activeTabKey;

          return (
            <Heading
              level={4}
              textColor={checked ? 'text-secondary' : 'text-gray-600'}
              fontFamily="font-sans"
              fontWeight="font-semibold"
            >
              {levelDefinition.displayName}
            </Heading>
          );
        }}
      />

      <FlatCard className={'bg-white mt-4'} paddingClassName={'p-6'}>
        <div className="flex justify-between gap-4">
          <div className="flex-1 relative">
            <RootCauseList
              header={sectionDescription}
              metricDefinition={metricDefinition}
              groupResults={currentInsight.groupResults}
              selectedIndex={activeItemIndex}
              onItemClick={(index: number) => {
                setActiveItemIndex(index);
                setActiveGroupResult(currentInsight.groupResults[index]);
              }}
              focusAreaDefinition={focusArea}
            />
          </div>

          <div className={'flex-1'}>
            <div ref={imageRef}>
              <RootCauseGraphPart
                type={'root-cause'}
                activeGroupResult={activeGroupResult}
                focusArea={focusArea}
                metricDefinition={metricDefinition}
                performanceMetricDefinition={null}
                dimension={currentInsight.dimension}
                referenceLevel={currentInsight.referenceLevel}
                treatmentLevel={currentInsight.treatmentLevel}
                imageRef={imageRef}
                key={JSON.stringify(activeGroupResult)}
                graphClassName={'h-[30rem]'}
              />
            </div>
            {explanationHook.explanation && (
              <>
                <div className={'h-[1px] bg-gray-200 mt-5 mb-5'} />
                <S2Typography
                  translatedContent={language.chartInterpretationTitle()}
                  variant={'small-label-alt'}
                  textColorVariant={'gray-900'}
                />
                <S2Typography
                  variant={'body-6'}
                  textColorVariant={'gray-900'}
                  translatedContent={language.dangerousUserInput({
                    value: explanationHook.explanation,
                  })}
                />
              </>
            )}
          </div>
        </div>
      </FlatCard>

      {/* TODO: remove this if we can, doesn't seem to do anything */}
      <ShareLinkModal
        shareLink={''}
        showModal={showShareModal}
        setShowModal={setShowShareModal}
      />
    </div>
  );
};

export interface RootCauseGraphPartProps
  extends RootCauseGraphPartSerializableProps {
  imageRef?: React.RefObject<HTMLInputElement | null>;
}

// eslint-disable-next-line complexity
export const RootCauseGraphPart = ({
  focusArea,
  metricDefinition,
  activeGroupResult,
  treatmentLevel,
  referenceLevel,
  dimension,
  imageRef,
  type,
  performanceMetricDefinition,
  graphClassName,
  hideDownload,
  calibrationFraming,
}: RootCauseGraphPartProps) => {
  const { getLevelByColumnValue, getAllLevelsForDimension } =
    useDimensionConfig();
  const observationHook = useObservation();
  const language = useLanguage();

  const controlVariableDefinitions = useMemo(() => {
    return observationHook.myObservation?.focusAreas.find((fa) => {
      return fa.focusArea === focusArea.focusArea;
    })?.controlVariables;
  }, [focusArea, observationHook]);

  const referenceGroupLevelDefinition = useMemo(() => {
    if (!activeGroupResult) {
      return undefined;
    }

    return isDiversionBarGraphGroupResult(activeGroupResult)
      ? undefined
      : activeGroupResult.plot_type === 'BinScatterControlledVsUncontrolled'
        ? getAllLevelsForDimension(dimension).find(
            (level) =>
              level.displayName === activeGroupResult.reference_level.name,
          )
        : getLevelByColumnValue(dimension, referenceLevel);
  }, [
    activeGroupResult,
    dimension,
    getAllLevelsForDimension,
    getLevelByColumnValue,
    referenceLevel,
  ]);

  const controlVariableDisplay = useMemo(() => {
    if (!activeGroupResult || !controlVariableDefinitions) {
      return '';
    }

    return getControlVariableDisplayText(
      activeGroupResult,
      controlVariableDefinitions ?? [],
    );
  }, [activeGroupResult, controlVariableDefinitions]);

  const treatmentGroupLevelDefinition = useMemo(() => {
    if (!activeGroupResult) {
      return undefined;
    }

    return isDiversionBarGraphGroupResult(activeGroupResult)
      ? undefined
      : activeGroupResult.plot_type === 'BinScatterControlledVsUncontrolled'
        ? getAllLevelsForDimension(dimension).find(
            (level) =>
              level.displayName === activeGroupResult.treatment_level.name,
          )
        : getLevelByColumnValue(dimension, treatmentLevel);
  }, [
    activeGroupResult,
    dimension,
    getAllLevelsForDimension,
    getLevelByColumnValue,
    treatmentLevel,
  ]);

  const referenceGroupColor = useMemo(() => {
    if (!referenceGroupLevelDefinition) {
      return 'bg-black';
    }
    return ConfigColors[referenceGroupLevelDefinition.color].Primary.bg;
  }, [referenceGroupLevelDefinition]);
  const treatmentGroupColor = useMemo(() => {
    if (!treatmentGroupLevelDefinition) {
      return 'bg-black';
    }
    return ConfigColors[treatmentGroupLevelDefinition.color].Primary.bg;
  }, [treatmentGroupLevelDefinition]);

  const activeKeyConfig = useMemo(() => {
    return getLevelByColumnValue(dimension, treatmentLevel);
  }, [getLevelByColumnValue, dimension, treatmentLevel]);

  const overridingYAxisText = useMemo(() => {
    const textOutputs = extractRootCauseTextOutputs(activeGroupResult);
    switch (type) {
      case 'root-cause':
        return getTranslatedContentAsString(
          language.yAxisForExplanatoryFactors({
            metricDefinition,
            groupResult: activeGroupResult,
          }),
        );
      case 'bias-test-visualization':
        return typeof textOutputs !== 'string' &&
          textOutputs &&
          activeKeyConfig &&
          activeGroupResult.visualizationAttributes
          ? getTranslatedContentAsString(
              language.yAxisForBiasVisualization({
                metricDefinition,
                performanceMetricDefinition: performanceMetricDefinition,
                treatmentLevel: activeKeyConfig,
                biasTestId:
                  textOutputs.biasTestId !== undefined
                    ? textOutputs.biasTestId
                    : null,
                taskMetricModifier:
                  activeGroupResult.visualizationAttributes
                    .taskMetricDisplayModifier ?? null,
                taskMetricDisplayName:
                  activeGroupResult.visualizationAttributes
                    .taskMetricDisplayName,
              }),
            )
          : '';
      default:
        throw guardExhaustive(type);
    }
  }, [
    activeGroupResult,
    activeKeyConfig,
    metricDefinition,
    performanceMetricDefinition,
    language,
    type,
  ]);

  // eslint-disable-next-line complexity
  const overridingXAxisText = useMemo(() => {
    const textOutputs = extractRootCauseTextOutputs(activeGroupResult);
    switch (type) {
      case 'root-cause':
        return typeof textOutputs !== 'string' && textOutputs
          ? textOutputs.variableToOutputMap[textOutputs.controlVariable]
          : null;
      case 'bias-test-visualization':
        return typeof textOutputs !== 'string' &&
          textOutputs &&
          activeKeyConfig &&
          activeGroupResult.visualizationAttributes
          ? getTranslatedContentAsString(
              language.xAxisForBiasVisualization({
                metricDefinition,
                treatmentLevel: activeKeyConfig,
                biasTestId:
                  textOutputs.biasTestId !== undefined
                    ? textOutputs.biasTestId
                    : null,
                framing: activeGroupResult.visualizationAttributes.framing,
                skillFraming:
                  activeGroupResult.visualizationAttributes.skillFraming,
                calibrationFraming: calibrationFraming ?? 'bias',
              }),
            )
          : null;
      default:
        throw guardExhaustive(type);
    }
  }, [
    activeGroupResult,
    activeKeyConfig,
    metricDefinition,
    language,
    type,
    calibrationFraming,
  ]);

  const analytics = useAnalytics();

  const handleOnDownloadCb = useCallback(() => {
    let plotType: DownloadableCharts = activeGroupResult.plot_type;

    if ('decompositionType' in activeGroupResult) {
      plotType = activeGroupResult.decompositionType;
    }

    analytics.trackEvent({
      eventName: 'Chart Downloaded',
      eventProperties: { type: plotType },
    });
  }, [analytics, activeGroupResult]);

  return (
    <>
      {isDiversionBarGraphGroupResult(activeGroupResult) && (
        <RootCauseDisparityBarChart
          controlVariableDisplay={controlVariableDisplay}
          groupResult={activeGroupResult}
          treatmentLevel={treatmentLevel}
          download={
            !hideDownload && (
              <DownloadButtonWrapper
                imageRef={imageRef}
                focusArea={focusArea.focusArea}
                activeKeyConfig={activeKeyConfig}
                onDownloadCb={handleOnDownloadCb}
              />
            )
          }
          yAxisText={overridingYAxisText}
          graphClassName={graphClassName}
        />
      )}
      {activeGroupResult.plot_type === 'BinScatterControlledVsUncontrolled' && (
        <RootCauseBinScatterChart
          metricDefinition={metricDefinition}
          dimension={dimension}
          groupResult={activeGroupResult}
          focusArea={focusArea}
          referenceGroupColor={referenceGroupColor}
          referenceGroupDisplayName={activeGroupResult.reference_level.name}
          treatmentGroupColor={treatmentGroupColor}
          treatmentGroupDisplayName={activeGroupResult.treatment_level.name}
          download={
            !hideDownload && (
              <DownloadButtonWrapper
                imageRef={imageRef}
                focusArea={focusArea.focusArea}
                activeKeyConfig={activeKeyConfig}
                onDownloadCb={handleOnDownloadCb}
              />
            )
          }
          yAxisLabel={overridingYAxisText}
          xAxisLabel={overridingXAxisText}
        />
      )}

      {activeGroupResult.plot_type === 'lineChart' &&
        activeGroupResult.decompositionType === 'Oaxaca Beta' && (
          <RootCauseBinScatterChart
            metricDefinition={metricDefinition}
            dimension={dimension}
            groupResult={activeGroupResult}
            focusArea={focusArea}
            referenceGroupColor={referenceGroupColor}
            referenceGroupDisplayName={
              activeGroupResult.referenceDimensionLevelColumnValue
            }
            treatmentGroupColor={treatmentGroupColor}
            treatmentGroupDisplayName={
              activeGroupResult.treatmentDimensionLevelColumnValue
            }
            download={
              !hideDownload && (
                <DownloadButtonWrapper
                  imageRef={imageRef}
                  focusArea={focusArea.focusArea}
                  activeKeyConfig={activeKeyConfig}
                  onDownloadCb={handleOnDownloadCb}
                />
              )
            }
            yAxisLabel={overridingYAxisText}
            xAxisLabel={overridingXAxisText}
          />
        )}

      {activeGroupResult.plot_type === 'GenericBarGraph' && (
        <RootCauseGenericBarChart
          result={activeGroupResult}
          treatmentLevel={treatmentLevel}
          download={
            !hideDownload && (
              <DownloadButtonWrapper
                imageRef={imageRef}
                focusArea={focusArea.focusArea}
                activeKeyConfig={activeKeyConfig}
                onDownloadCb={handleOnDownloadCb}
              />
            )
          }
          yAxisText={overridingYAxisText}
          graphClassName={graphClassName}
        />
      )}
      {activeGroupResult.plot_type === 'barChart' && (
        <OaxacaBarChart
          result={activeGroupResult}
          download={
            !hideDownload && (
              <DownloadButtonWrapper
                imageRef={imageRef}
                focusArea={focusArea.focusArea}
                activeKeyConfig={activeKeyConfig}
                onDownloadCb={handleOnDownloadCb}
              />
            )
          }
          focusArea={focusArea.focusArea}
        />
      )}
      {activeGroupResult.plot_type === 'BoxPlot' && (
        <ThresholdAnalysisBoxPlot
          xAxisText={overridingXAxisText ?? ''}
          result={activeGroupResult}
          treatmentExplanationTranslatedContent={language.thresholdTreatmentGroupExplanation(
            {
              treatmentThresholdDetails:
                activeGroupResult.treatment_threshold_details,
              treatmentLevelDefinition: treatmentGroupLevelDefinition ?? null,
              calibrationFraming: calibrationFraming ?? 'bias',
              metricDefinition: metricDefinition,
            },
          )}
          referenceExplanationTranslatedContent={language.thresholdReferenceGroupExplanation(
            {
              referenceThresholdDetails:
                activeGroupResult.reference_threshold_details,
              referenceLevelDefinition: referenceGroupLevelDefinition ?? null,
              calibrationFraming: calibrationFraming ?? 'bias',
              metricDefinition: metricDefinition,
            },
          )}
          gapExplanationTranslatedContent={language.thresholdGapExplanation({
            treatmentThresholdDetails:
              activeGroupResult.treatment_threshold_details,
            referenceThresholdDetails:
              activeGroupResult.reference_threshold_details,
            metricDefinition: metricDefinition,
          })}
        />
      )}
    </>
  );
};
